import {
  useDaily,
  useLocalSessionId,
  useParticipantIds,
} from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

interface UseTranscriptionPermissionsArgs {
  /**
   * Only process permissions when in an active meeting
   */
  isInMeeting: boolean;
}

/**
 * Hook that automatically grants transcription admin permissions to remote call participants.
 *
 * This ensures that unauthenticated users (patients) can access the closed captions (CC)
 * button and initiate transcription.
 *
 * Behavior:
 * - Monitors participant changes using useParticipantIds()
 * - Filters out the local participant (self) since meeting owners already have all admin privileges
 * - When new remote participants join, grants them canAdmin: 'transcription' permission
 * - Tracks which participants have been processed to avoid redundant updates
 * - Only processes participants when in 'joined-meeting' state
 *
 * Edge Cases Handled:
 * - Participant rejoin: Permissions are idempotent (safe to grant multiple times)
 * - Multiple rapid joins: useRef prevents duplicate processing
 * - Race conditions: Only processes when callObject is available
 * - Self-permission: Filters out local participant to avoid unnecessary API calls
 *
 * @see https://docs.daily.co/guides/products/transcription#setting-transcription-permissions
 */
export function useTranscriptionPermissions({
  isInMeeting,
}: UseTranscriptionPermissionsArgs) {
  const callObject = useDaily();
  const participantIds = useParticipantIds();
  const localSessionId = useLocalSessionId();
  const router = useRouter();
  const roomId =
    typeof router.query.roomId === 'string' ? router.query.roomId : '';

  // Track which participants we've already granted permissions to
  // This prevents redundant updateParticipant calls
  const processedParticipants = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!callObject || !isInMeeting || participantIds.length === 0) {
      return;
    }

    // Filter out local participant - meeting owners already have all admin privileges
    const remoteParticipantIds = participantIds.filter(
      (id) => id !== localSessionId,
    );

    if (remoteParticipantIds.length === 0) {
      return;
    }

    remoteParticipantIds.forEach((participantId) => {
      // Skip if we've already processed this participant
      if (processedParticipants.current.has(participantId)) {
        return;
      }

      try {
        callObject.updateParticipant(participantId, {
          updatePermissions: {
            canAdmin: ['transcription'],
          },
        });

        // Mark as processed
        processedParticipants.current.add(participantId);

        datadogLogs.logger.info(
          'Granted transcription permissions',
          callLogContext('transcription', {
            roomId,
            participantType: 'provider',
            participantId,
            totalParticipants: participantIds.length,
            remoteParticipants: remoteParticipantIds.length,
            processedCount: processedParticipants.current.size,
          }),
        );
      } catch (error) {
        datadogLogs.logger.error(
          'Failed to grant permissions',
          callLogContext('transcription', {
            roomId,
            participantType: 'provider',
            participantId,
          }),
          error instanceof Error ? error : undefined,
        );
      }
    });
  }, [callObject, participantIds, localSessionId, isInMeeting, roomId]);

  // Clean up processed participants when meeting ends
  useEffect(() => {
    if (!isInMeeting && processedParticipants.current.size > 0) {
      datadogLogs.logger.info(
        'Meeting ended, clearing processed participants',
        callLogContext('transcription', {
          roomId,
          participantType: 'provider',
          processedCount: processedParticipants.current.size,
        }),
      );
      processedParticipants.current.clear();
    }
  }, [isInMeeting, roomId]);
}
